import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Paper,
  Typography,
  Switch,
  Button,
  Grid,
  makeStyles,
  Slider,
  Box,
  useTheme
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import InfoIcon from "@material-ui/icons/Info";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useTitle } from "../../hooks/useTitle";

const useStyles = makeStyles((theme) => ({
  paper: { 
    padding: theme.spacing(2), 
    marginTop: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  section: { marginBottom: theme.spacing(3) },
  headerActions: { display: "flex", gap: theme.spacing(1) },
  sliderContainer: {
    width: "100%",
    padding: "0 10px"
  },
  infoBox: {
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#e3f2fd',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
}));

const defaultReminders = {
  enabled: false,
  dayReminders: [],
  reminder5Hours: 1
};

const MyReminders = () => {
  useTitle("Configurar Recordatorios");
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const [doctorReminders, setDoctorReminders] = useState(defaultReminders);
  const [originalReminders, setOriginalReminders] = useState(defaultReminders);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const hasChanges = JSON.stringify(doctorReminders) !== JSON.stringify(originalReminders);
  
  const normalizeReminders = (reminder) => {
    const dayReminders = [
      reminder.reminder1Days,
      reminder.reminder2Days,
      reminder.reminder3Days,
      reminder.reminder4Days
    ].filter((d) => d !== null && d !== undefined);

    return {
      ...defaultReminders,
      ...reminder,
      dayReminders
    };
  };
  
  const toBackendPayload = (reminder, doctorId) => {
    const [r1, r2, r3, r4] = reminder.dayReminders || [];
    return {
      id: reminder.id,
      doctorId,
      enabled: reminder.enabled,
      reminder5Hours: reminder.reminder5Hours,
      reminder1Days: r1 || null,
      reminder2Days: r2 || null,
      reminder3Days: r3 || null,
      reminder4Days: r4 || null
    };
  };

  useEffect(() => {
    const fetchReminders = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/doctorReminders", { params: { doctorId: user.id } });
        if (data && data.length > 0) {
          const reminder = data[0];
          setDoctorReminders(normalizeReminders(reminder));
          setOriginalReminders(normalizeReminders(reminder));
        }
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    };

    fetchReminders();
  }, [user.id]);

  const handleChange = (field, value) => {
    let newReminders = { ...doctorReminders, [field]: value };

    const days = ["reminder1Days", "reminder2Days", "reminder3Days", "reminder4Days"];
    for (let i = 1; i < days.length; i++) {
      const prev = newReminders[days[i - 1]];
      const current = newReminders[days[i]];
      if (prev !== null && current !== null && current <= prev) {
        newReminders[days[i]] = prev + 1;
      }
    }

    setDoctorReminders(newReminders);
  };  
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = toBackendPayload(doctorReminders, user.id);

      if (doctorReminders.id) {
        const { data } = await api.put(`/doctorReminders/${doctorReminders.id}`, payload);
        setDoctorReminders(normalizeReminders(data));
        setOriginalReminders(normalizeReminders(data));
      } else {
        const { data } = await api.post("/doctorReminders", payload);
        setDoctorReminders(normalizeReminders(data));
        setOriginalReminders(normalizeReminders(data));
      }

      toast.success("Recordatorios guardados correctamente");
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const addDayReminder = () => {
    if (doctorReminders.dayReminders.length < 4) {
      let newReminders = [...doctorReminders.dayReminders, 1];

      newReminders.sort((a, b) => a - b);

      for (let i = 0; i < newReminders.length - 1; i++) {
        if (newReminders[i] >= newReminders[i + 1]) {
          newReminders[i + 1] = newReminders[i] + 1;
        }
      }

      if (newReminders[newReminders.length - 1] > 30) {
          newReminders[newReminders.length - 1] = 30;
          for (let i = newReminders.length - 1; i > 0; i--) {
             if (newReminders[i] <= newReminders[i - 1]) {
                newReminders[i - 1] = newReminders[i] - 1;
             }
          }
      }

      setDoctorReminders({
        ...doctorReminders,
        dayReminders: newReminders
      });
    }
  };

  const removeDayReminder = (index) => {
    const newReminders = [...doctorReminders.dayReminders];
    newReminders.splice(index, 1);
    setDoctorReminders({ ...doctorReminders, dayReminders: newReminders });
  };

  const adjustDayReminders = (days, max = 30) => {
    let set = new Set();
    let result = [];

    for (let d of days) {
      let value = d;

      while (set.has(value) && value > 1) {
        value--;
      }

      value = Math.max(1, value);

      set.add(value);
      result.push(value);
    }

    return result.sort((a, b) => a - b).filter((v) => v <= max);
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Estrategia de Recordatorios</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.headerActions}>
            <Button
              color="primary"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!hasChanges || saving || loading}
              onClick={handleSave}
            >
              Guardar cambios
            </Button>
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.paper}>
        <Grid container spacing={2} className={classes.section} alignItems="center">
          <Grid item xs={12} sm={8}>
            <Typography variant="h6" gutterBottom>
              Asistente Virtual de Citas
            </Typography>
            <Typography variant="body1">
              Habilitar el envío automático de mensajes de WhatsApp para reducir el ausentismo.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4} style={{ textAlign: 'right' }}>
            <Switch
              checked={doctorReminders.enabled}
              onChange={(e) => handleChange("enabled", e.target.checked)}
              color="primary"
            />
          </Grid>
        </Grid>

        {doctorReminders.enabled && (
          <>
            <div className={classes.infoBox}>
              <div>
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: theme.palette.primary.main }} gutterBottom>
                  ¿Cómo funciona su estrategia?
                </Typography>
                <Typography variant="body2" component="div">
                  El sistema contactará al paciente en dos momentos estratégicos:
                  <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li><strong>Planeación (Días antes):</strong> Mantiene la cita presente en la agenda del paciente para evitar que comprometa ese horario.</li>
                    <li><strong>Traslado (El mismo día):</strong> Sirve como una alerta final para que el paciente se prepare y salga a tiempo hacia el consultorio.</li>
                  </ol>
                </Typography>
              </div>
            </div>

            <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16 }}>
              1. Recordatorio de "Salida" (Mismo día)
            </Typography>
            <Grid container spacing={3} className={classes.section} alignItems="center">
              <Grid item xs={12} md={5}>
                <Typography variant="body2" color="textSecondary">
                  Recomendamos avisar 2 o 3 horas antes, justo cuando el paciente debería prepararse para salir.
                </Typography>
                <Typography variant="h6" color="primary" style={{ marginTop: 8 }}>
                  {doctorReminders.reminder5Hours || 1} hora(s) antes
                </Typography>
              </Grid>
              <Grid item xs={12} md={7}>
                <div className={classes.sliderContainer}>
                  <Slider
                    value={doctorReminders.reminder5Hours || 1}
                    onChange={(e, value) => handleChange("reminder5Hours", value)}
                    step={1}
                    min={1}
                    max={5}
                    marks
                    valueLabelDisplay="auto"
                    disabled={loading || saving}
                  />
                </div>
              </Grid>
            </Grid>

            <div style={{ marginTop: 32, marginBottom: 16 }}>
              <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                2. Seguimiento previo a la consulta
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Configure los avisos previos para que el paciente no olvide su cita.
              </Typography>
            </div>

            {doctorReminders.dayReminders
            .slice()
            .sort((a, b) => b - a)
            .map((value, renderIndex, arr) => {
              const i = doctorReminders.dayReminders.indexOf(value);

              let labelTitle = "";
              if (arr.length === 1) {
                labelTitle = "Aviso único de recordatorio";
              } else if (renderIndex === 0) {
                labelTitle = "Aviso inicial (Anticipación)";
              } else if (renderIndex === arr.length - 1) {
                labelTitle = "Aviso final (Cercano a la cita)";
              } else {
                labelTitle = "Recordatorio de refuerzo";
              }

              return (
                <Grid container spacing={3} key={i} alignItems="center" style={{ marginBottom: 16, borderBottom: '1px solid rgba(0,0,0,0.1)', paddingBottom: 16 }}>
                  <Grid item xs={12} md={5}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {labelTitle}
                    </Typography>
                    <Typography variant="body2">
                      Enviar <strong>{value} día{value !== 1 ? "s" : ""}</strong> antes de la cita
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={9} md={5}>
                    <div className={classes.sliderContainer}>
                      <Slider
                        value={value}
                        onChange={(e, val) => {
                          const newReminders = [...doctorReminders.dayReminders];
                          newReminders[i] = val;
                          newReminders.sort((a, b) => a - b);
                          for (let j = 1; j < newReminders.length; j++) {
                            if (newReminders[j] <= newReminders[j - 1]) {
                              newReminders[j] = Math.min(newReminders[j - 1] + 1, 30);
                            }
                          }
                          for (let j = newReminders.length - 2; j >= 0; j--) {
                            if (newReminders[j] >= newReminders[j + 1]) {
                              newReminders[j] = Math.max(newReminders[j + 1] - 1, 1);
                            }
                          }
                          setDoctorReminders({ ...doctorReminders, dayReminders: newReminders });
                        }}
                        step={1}
                        min={1}
                        max={30}
                        marks
                        valueLabelDisplay="auto"
                        disabled={loading || saving}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={3} md={2} style={{ textAlign: 'right' }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={() => removeDayReminder(i)}
                    >
                      Eliminar
                    </Button>
                  </Grid>
                </Grid>
              );
            })}
            
            <Box mt={2} mb={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={addDayReminder}
                disabled={doctorReminders.dayReminders.length >= 4}
              >
                Programar otro día de aviso
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default MyReminders;
